import { useState, useEffect } from "react";
import { Button, Spinner } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import moment from "moment";

const BASE = "https://dailydish.in";
const SALES_API = `${BASE}/api/admin/plan/sales-tracker`;
const PRODUCTS_API = `${BASE}/api/production/products`;

const SESSION_LABEL = { b: "Breakfast", l: "Lunch", d: "Dinner" };
const SESSION_STYLE = {
  b: { active: { background: "#FAEEDA", color: "#BA7517", border: "1.5px solid #EF9F27" }, pill: { background: "#FAEEDA", color: "#BA7517" } },
  l: { active: { background: "#E1F5EE", color: "#085041", border: "1.5px solid #1D9E75" }, pill: { background: "#E1F5EE", color: "#085041" } },
  d: { active: { background: "#E6F1FB", color: "#185FA5", border: "1.5px solid #185FA5" }, pill: { background: "#E6F1FB", color: "#185FA5" } },
};

export default function OrdersTab({ session, onSessionChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const today = moment().format("YYYY-MM-DD");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [salesRes, productsRes] = await Promise.all([
          axios.get(SALES_API, { params: { date: today, session: SESSION_LABEL[session] } }),
          axios.get(PRODUCTS_API),
        ]);

        const salesData = salesRes.data.success ? salesRes.data.data : [];
        const prodProducts = productsRes.data.success ? productsRes.data.data : [];

        // Build image map from production products (keyed by lowercase foodname)
        const imgMap = {};
        prodProducts.forEach(p => { if (p.foodItemImage) imgMap[p.name.toLowerCase()] = p.foodItemImage; });

        // Aggregate sales by foodName
        const itemMap = new Map();
        salesData.forEach(item => {
          const pre = item.precutoffConfirmed || 0;
          const win = item.windowFixedQty || 0;
          if (pre > 0 || win > 0) {
            const ex = itemMap.get(item.foodName);
            if (ex) { ex.pre += pre; ex.win = Math.max(ex.win, win); }
            else itemMap.set(item.foodName, { name: item.foodName, pre, win });
          }
        });

        const result = Array.from(itemMap.values())
          .map(r => ({ ...r, total: r.pre + r.win, image: imgMap[r.name.toLowerCase()] || "" }))
          .sort((a, b) => b.total - a.total);

        setRows(result);
      } catch { toast.error("Failed to load orders"); }
      finally { setLoading(false); }
    };
    load();
  }, [session, today]);

  const totalUnits = rows.reduce((s, r) => s + r.total, 0);

  return (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 style={{ fontWeight: 700, marginBottom: 2 }}>Today's Orders</h5>
          <div style={{ fontSize: 13, color: "#888" }}>{today} — live from plan tracker</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {["b", "l", "d"].map(s => (
            <button key={s} onClick={() => onSessionChange(s)} style={{
              padding: "8px 20px", borderRadius: 20, fontWeight: 500, fontSize: 14, cursor: "pointer",
              transition: "all 0.15s",
              ...(session === s ? SESSION_STYLE[s].active : { background: "#fff", color: "#888", border: "1.5px solid #e0e0e0" }),
            }}>
              {SESSION_LABEL[s]}
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      {!loading && rows.length > 0 && (
        <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
          <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 10, padding: "12px 20px", minWidth: 110 }}>
            <div style={{ fontSize: 11, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Products</div>
            <div style={{ fontSize: 26, fontWeight: 700 }}>{rows.length}</div>
          </div>
          <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 10, padding: "12px 20px", minWidth: 110 }}>
            <div style={{ fontSize: 11, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Total units</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: "#1D9E75" }}>{totalUnits}</div>
          </div>
        </div>
      )}

      {/* Orders table */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #eee", overflow: "hidden" }}>
        {loading ? (
          <div className="text-center py-5"><Spinner /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-5" style={{ color: "#aaa" }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>📋</div>
            <div style={{ fontWeight: 500 }}>No orders for {SESSION_LABEL[session]}</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>Orders come from the plan tracker for today</div>
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f8f8f8", borderBottom: "1px solid #eee" }}>
                <th style={{ padding: "12px 20px", fontSize: 12, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em", textAlign: "left" }}>Product</th>
                <th style={{ padding: "12px 20px", fontSize: 12, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em", textAlign: "right" }}>Pre-order</th>
                <th style={{ padding: "12px 20px", fontSize: 12, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em", textAlign: "right" }}>Window</th>
                <th style={{ padding: "12px 20px", fontSize: 12, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em", textAlign: "right" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f5f5f5", transition: "background 0.1s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
                  onMouseLeave={e => e.currentTarget.style.background = ""}
                >
                  <td style={{ padding: "14px 20px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      {row.image
                        ? <img src={row.image} alt={row.name} style={{ width: 52, height: 52, objectFit: "cover", borderRadius: 10, border: "1px solid #eee", flexShrink: 0 }} />
                        : <div style={{ width: 52, height: 52, background: "#f5f5f5", borderRadius: 10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>🍽️</div>
                      }
                      <span style={{ fontWeight: 600, fontSize: 15 }}>{row.name}</span>
                    </div>
                  </td>
                  <td style={{ padding: "14px 20px", textAlign: "right", color: "#888", fontSize: 15 }}>{row.pre}</td>
                  <td style={{ padding: "14px 20px", textAlign: "right", color: "#888", fontSize: 15 }}>{row.win}</td>
                  <td style={{ padding: "14px 20px", textAlign: "right" }}>
                    <span style={{ fontSize: 24, fontWeight: 700, color: "#1D9E75" }}>{row.total}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
